import { IModel } from "@/@types/models/model";
import { coreFields } from "./helpers/coreFields";
import { commonFields } from "./helpers/commonFields";
import { translate } from "ol/transform";

const User: IModel = {
  title: "POI",
  apiPath: "poi",
  httpUpdateVerb: "post",
  forms: {
    default: {
      fields: ["naziv", "godina", "opis", "info_link"],
    },
    overlay: {
      fields: ["naziv", "poi_tip_id", "hps_info_url"],
    },
    insert: {
      fields: [
        {
          source: "poi_tip_id",
          type: "picker",
          readonly: false,
          items: {
            values: [20, 40, 50, 60, 70, 71, 72, 73, 81, 82, 83, 84, 85, 86, 90, 91],
            labels: [
              "common.hgss",
              "common.penjaliste",
              "common.opasnost",
              "common.vrh",
              "common.vidikovac",
              "common.prir_izvor",
              "common.prir_slap",
              "common.prir_spilja",
              "common.kult_arheo",
              "common.kult_mlin",
              "common.kult_muzej",
              "common.kult_sakralni",
              "common.kult_spomenik",
              "common.kult_utvrda",
              "common.pocetak-staze",
              "common.odmoriste"
            ],
          },
        },
        { source: "hgss_projekt_sifra", readonly: false },
        { source: "naziv", readonly: false },
        { source: "visina_m", readonly: false},
        { source: "hgss_napomena", readonly: false },
        "wkt",
      ],
    },
    hgss_user_on_hgss_data: {
      fields: [
        "id",
        { source: "naziv", readonly: false },
        { source: "poi_tip_id", readonly: false, type: "picker" },
        { source: "hgss_projekt_sifra", readonly: false },
        { source: "hgss_napomena", readonly: false },
        { source: "hps_info_url", readonly: false },
        { source: "hps_napomena", readonly: true },
        { source: "visina_m", readonly: false },
        "image_filepath",
        "image_credits",
        "panorama_title",
        "panorama_credits",
        "panorama_filename",
        "livecam_url",
        "photo",
        "panorama",
        "photo_active",
        "panorama_active",
        "livecam_active",
        "opis_hr",
        "opis_en"
      ],
    },
    hgss_user_on_hps_data: {
      fields: [
        "id",
        { source: "naziv", readonly: true },
        { source: "poi_tip_id", readonly: false, type: "picker" },
        { source: "hgss_projekt_sifra", readonly: true },
        { source: "hgss_napomena", readonly: false },
        { source: "hps_info_url", readonly: false },
        { source: "hps_napomena", readonly: true },
        "image_filepath",
        "image_credits",
        "panorama_title",
        "panorama_credits",
        "panorama_filename",
        "livecam_url",
        "photo",
        "panorama",
        "photo_active",
        "panorama_active",
        "livecam_active",
        "opis_hr",
        "opis_en"
      ],
    },
    hps_user_on_hps_data: {
      fields: [
        "id",
        { source: "naziv", readonly: false },
        { source: "poi_tip_id", type: "text" },
        { source: "hgss_projekt_sifra", readonly: true },
        { source: "hgss_napomena", readonly: true },
        { source: "hps_info_url", readonly: false },
        { source: "hps_napomena", readonly: false },
        { source: "image_credits", readonly: true },
        { source: "image_filepath", readonly: true },
        { source: "panorama_title", readonly: true },
        { source: "panorama_credits", readonly: true },
        { source: "panorama_filename", readonly: true },
        { source: "livecam_url", readonly: true },
        { source: "photo", readonly: true },
        { source: "panorama", readonly: true },
        { source: "photo_active", readonly: true },
        { source: "panorama_active", readonly: true },
        { source: "livecam_active", readonly: true },
        { source: "opis_hr", readonly: true},
        { source: "opis_en", readonly: true}
      ],
    },
    hps_user_on_hgss_data: {
      fields: [
        "id",
        { source: "naziv", readonly: true },
        { source: "poi_tip_id", type: "text" },
        { source: "hgss_projekt_sifra", readonly: true },
        { source: "hgss_napomena", readonly: true },
        { source: "hps_info_url", readonly: true },
        { source: "hps_napomena", readonly: false },
        { source: "image_credits", readonly: true },
        { source: "image_filepath", readonly: true },
        { source: "panorama_title", readonly: true },
        { source: "panorama_credits", readonly: true },
        { source: "panorama_filename", readonly: true },
        { source: "livecam_url", readonly: true },
        { source: "photo", readonly: true },
        { source: "panorama", readonly: true },
        { source: "photo_active", readonly: true },
        { source: "panorama_active", readonly: true },
        { source: "livecam_active", readonly: true },
        { source: "opis_hr", readonly: true},
        { source: "opis_en", readonly: true}
      ]
    },
  },
  listViews: {
    default: {
      fields: [
        "id",
        "naziv",
        "poi_tip_id",
        "visina_m",
        "podrucje",
        "planina",
        "hps_info_url",
        "hps_napomena",
        "hgss_napomena",
        "hgss_projekt_sifra",
        "owner_organisation",
        "created_on",
        "modified_on",
        "data_source",
        "active",
      ],
      hidden: ["id", "data_source", "active"],
    },
  },
  fields: [
    coreFields.id,
    coreFields.created_on,
    coreFields.created_by,
    coreFields.modified_on,
    coreFields.modified_by,
    commonFields.hgss_modified_on,
    commonFields.hgss_modified_by,
    commonFields.hps_modified_on,
    commonFields.hps_modified_by,
    commonFields.hgss_zemljovid,
    commonFields.podrucje,
    commonFields.planina,
    {
      title: "godina",
      source: "godina",
      ttoken: "data.godina",
      type: "numeric",
    },
    {
      title: "opis",
      source: "opis",
      ttoken: "data.opis",
      type: "text",
      validation: {
        maxLength: 250,
      },
    },
    {
      title: "Naziv",
      source: "naziv",
      ttoken: "data.naziv",
      type: "text",
      width: "full",
      validation: {
        maxLength: 100,
      },
      readonly: true,
    },
    {
      title: "broj_komentara",
      source: "broj_komentara",
      ttoken: "data.broj_komentara",
      type: "numeric",
      readonly: true,
    },
    {
      title: "Info link",
      source: "info_link",
      ttoken: "data.info_link",
      type: "text",
      width: "full",
      validation: {
        maxLength: 250,
      },
    },
    commonFields.owner_organisation,
    commonFields.data_source,
    {
      title: "poi_tip_id",
      source: "poi_tip_id",
      ttoken: "data.poi_tip",
      type: "radio",
      row: true,
      items: {
        values: [20, 30, 31, 32, 33, 40, 50, 60, 70, 71, 72, 73, 81, 82, 83, 84, 85, 86, 90, 91],
        labels: [
          "common.hgss",
          "common.planinarska-kuca",
          "common.planinarska-kuca",
          "common.planinarski-dom",
          "common.planinarsko-skloniste",
          "common.penjaliste",
          "common.opasnost",
          "common.vrh",
          "common.vidikovac",
          "common.prir_izvor",
          "common.prir_slap",
          "common.prir_spilja",
          "common.kult_arheo",
          "common.kult_mlin",
          "common.kult_muzej",
          "common.kult_sakralni",
          "common.kult_spomenik",
          "common.kult_utvrda",
          "common.pocetak-staze",
          "common.odmoriste"
        ],
      },
      validation: {
        required: true,
      },
      readonly: true,
      translate: true,
    },
    commonFields.wkt,
    commonFields.proj,
    coreFields.active,
    {
      title: "HPS info url",
      source: "hps_info_url",
      ttoken: "data.hps_info_url",
      type: "url",
      width: "full",
      readonly: true,
    },
    {
      title: "HPS napomena",
      source: "hps_napomena",
      ttoken: "data.hps_napomena",
      type: "text",
      width: "full",
      readonly: true,
    },
    {
      title: "HGSS sifra projekta",
      source: "hgss_projekt_sifra",
      ttoken: "data.hgss_projekt_sifra",
      type: "text",
      readonly: true,
    },
    {
      title: "HGSS napomena",
      source: "hgss_napomena",
      ttoken: "data.hgss_napomena",
      type: "text",
      width: "full",
      readonly: true,
    },
    {
      title: "Panorame",
      source: "panoramas",
      ttoken: "data.panoramas",
      type: "array"
    },
    {
      title: "Photos",
      source: "photos",
      ttoken: "data.photos",
      type: "array"
    },
    {
      title: 'Photo',
      source: 'photo',
      ttoken: 'data.photo',
      type: 'images',
      maxFiles: 1
    },
    {
      title: 'Panorama',
      source: 'panorama',
      ttoken: 'data.panorama',
      type: 'images',
      maxFiles: 1
    },
    {
      title: "Livecams",
      source: "livecams",
      ttoken: "data.livecams",
      type: "array"
    },
    commonFields.podrucje,
    commonFields.planina,
    {
      title: "visina_m",
      source: "visina_m",
      ttoken: "data.visina_m",
      type: "wholenum",
      validation: {
        minValue: 0,
        maxValue: 1800
      },
      readonly: true
    },
    {
      title: "Geom changed",
      source: "geom_changed",
      ttoken: "data.geom_changed",
      type: "int"
    },
    {
      title: "Image filepath",
      source: "image_filepath",
      ttoken: "data.image_filepath",
      type: "text"
    },
    {
      title: "Image credits",
      source: "image_credits",
      ttoken: "data.credits",
      type: "text"
    },
    {
      title: "Image ID",
      source: "image_id",
      ttoken: "data.image_id",
      type: "numeric"
    },
    {
      title: "Livecam URL",
      source: "livecam_url",
      ttoken: "data.livecam_url",
      type: "text"
    },
    {
      title: "Livecam ID",
      source: "livecam_id",
      ttoken: "data.livecam_id",
      type: "numeric"
    },
    {
      title: "Panorama title",
      source: "panorama_title",
      ttoken: "data.panorama_title",
      type: "text"
    },
    {
      title: "Panorama credits",
      source: "panorama_credits",
      ttoken: "data.credits",
      type: "text"
    },
    {
      title: "Panorama filename",
      source: "panorama_filename",
      ttoken: "data.panorama_filename",
      type: "text"
    },
    {
      title: "Panorama ID",
      source: "panorama_id",
      ttoken: "data.panorama_id",
      type: "numeric"
    },
    {
      title: "Livecam Active",
      source: "livecam_active",
      ttoken: "data.active",
      type: "boolean",
      items: {
        labels: ["common.ne", "common.da"],
        values: [false, true],
        default: true
      },
      default: true,
      translate: true
    },
    {
      title: "Panorama Active",
      source: "panorama_active",
      ttoken: "data.active",
      type: "boolean",
      items: {
        labels: ["common.ne", "common.da"],
        values: [false, true],
        default: true
      },
      default: true,
      translate: true
    },
    {
      title: "Photo Active",
      source: "photo_active",
      ttoken: "data.active",
      type: "boolean",
      items: {
        labels: ["common.ne", "common.da"],
        values: [false, true],
        default: true
      },
      default: true,
      translate: true
    },
    {
      title: "Opis HR",
      source: "opis_hr",
      ttoken: "common.opis_hr",
      type: "multiline",
      rows: 10
    },
    {
      title: "Opis EN",
      source: "opis_en",
      ttoken: "common.opis_en",
      type: "multiline",
      rows: 10
    },
    {
      title: "Obilaznice",
      source: "obilaznice",
      ttoken: "common.obilaznice",
      type: "array"
    },
    {
      title: "Obilaznica KT",
      source: "is_obilaznica_kt",
      ttoken: "data.obilaznica_kt",
      type: "boolean",
      items: {
        labels: ["common.ne", "common.da"],
        values: [false, true],
        default: true
      },
      readonly: true
    },
  ],
};

export default User;
